import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { paymentAPI, customerAPI } from '../services/api';
import { useTranslation } from '../i18n';
import { ArrowLeft, Package, Check, Loader, CreditCard, Clock, Box, AlertTriangle, RefreshCw } from 'lucide-react';

export default function Pickup() {
  const [searchParams] = useSearchParams();
  const [pickCode, setPickCode] = useState(searchParams.get('code') || '');
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState({ S: 0.50, M: 0.70, L: 1.00 });
  const [step, setStep] = useState(1); // 1=enter code, 2=payment, 3=success, 4=confirming payment
  const [parcelInfo, setParcelInfo] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [paidNotOpened, setPaidNotOpened] = useState(false);
  const [doorOpen, setDoorOpen] = useState(null); // after pickup: true/false once known
  const navigate = useNavigate();
  const { t, isGeorgian } = useTranslation();

  // Check if returning from payment
  useEffect(() => {
    customerAPI.pricing().then(r => {
      const p = r.data;
      if (p && typeof p.S === 'number' && typeof p.M === 'number' && typeof p.L === 'number') setPricing(p);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const paymentResult = searchParams.get('payment');
    const savedCode = sessionStorage.getItem('pickup_code');
    if (paymentResult === 'success' && savedCode) {
      setPickCode(savedCode);
      sessionStorage.removeItem('pickup_code');
      pollPayment(savedCode);
      return;
    }
    // Auto-proceed if code is in URL (from QR scan)
    const urlCode = searchParams.get('code');
    if (urlCode && urlCode.length >= 4) {
      setPickCode(urlCode);
      // Auto-check the code
      (async () => {
        setLoading(true);
        try {
          const res = await paymentAPI.calculate(urlCode);
          setParcelInfo(res.data);
          setStep(2);
        } catch (e) {
          setError(e.response?.data?.detail || (isGeorgian ? 'ამანათი ვერ მოიძებნა' : 'Parcel not found'));
        }
        setLoading(false);
      })();
    }
  }, []);

  // After the box opens, keep watching the door until it's shut (up to 20 min).
  useEffect(() => {
    if (step !== 3 || !pickCode) return undefined;
    let stop = false, tries = 0;
    const tick = async () => {
      if (stop) return;
      tries += 1;
      try {
        const r = await paymentAPI.pickupStatus(pickCode);
        if (r.data.state === 'picked_up' && r.data.door_open !== null && r.data.door_open !== undefined) {
          setDoorOpen(r.data.door_open);
          if (r.data.door_open === false) return; // closed: done
        }
      } catch (e) { /* keep showing the warning */ }
      if (tries < 400) setTimeout(tick, 3000);
    };
    const t = setTimeout(tick, 1500);
    return () => { stop = true; clearTimeout(t); };
  }, [step, pickCode]);

  const showOpened = (boxName) => {
    setStep(3);
    setResult({ box_name: boxName || '?' });
    setLoading(false);
  };

  const pollPayment = async (code) => {
    // Returning from Keepz: Keepz confirms the payment to our server, then the
    // box opens. Show a "confirming" screen meanwhile and never the payment
    // form (it would invite paying twice).
    setStep(4);
    setError('');
    setPaidNotOpened(false);
    setLoading(true);
    let paidPolls = 0;
    for (let i = 0; i < 20; i++) {
      try {
        const r = await paymentAPI.pickupStatus(code);
        if (r.data.state === 'picked_up') { showOpened(r.data.box_name); return; }
        if (r.data.state === 'paid') {
          // Paid; the server is opening the box, which can take a while on a
          // slow link. Only after ~20 s offer the button, and keep watching
          // in case it opens after all.
          paidPolls += 1;
          if (paidPolls >= 10) {
            setLoading(false);
            setPaidNotOpened(true);
            watchForLateOpen(code);
            return;
          }
        }
      } catch (e) {
        if (e.response?.status === 429) {
          setLoading(false);
          setError(e.response.data?.detail || (isGeorgian ? 'ძალიან ბევრი მცდელობა, სცადეთ მოგვიანებით' : 'Too many attempts, try again later'));
          return;
        }
      }
      await new Promise(r => setTimeout(r, 2000));
    }
    setLoading(false);
    setError(isGeorgian
      ? 'გადახდის დადასტურება ჯერ არ მიგვიღია. თუ უკვე გადაიხადეთ, თავიდან ნუ გადაიხდით — დააჭირეთ „თავიდან შემოწმებას“.'
      : "We haven't received the payment confirmation yet. If you have already paid, do not pay again - press \"Check again\".");
  };

  // While "Open box" is offered, the box may still open on its own (slow
  // locker link): switch to the success screen when it does.
  const lateWatch = useRef(0);
  const watchForLateOpen = async (code) => {
    const me = ++lateWatch.current;
    for (let i = 0; i < 30; i++) {
      await new Promise(r => setTimeout(r, 3000));
      if (lateWatch.current !== me) return;
      try {
        const r = await paymentAPI.pickupStatus(code);
        if (lateWatch.current !== me) return;
        if (r.data.state === 'picked_up') { lateWatch.current += 1; showOpened(r.data.box_name); return; }
      } catch (e) { /* keep trying */ }
    }
  };
  useEffect(() => () => { lateWatch.current += 1; }, []);

  const handleOpenPaid = async () => {
    lateWatch.current += 1;
    setLoading(true);
    setError('');
    try {
      const r = await paymentAPI.openPaid(pickCode);
      showOpened(r.data.box_name);
    } catch (e) {
      if (e.response?.status === 404) {
        // Already opened a moment ago (older server): show it as opened.
        try {
          const st = await paymentAPI.pickupStatus(pickCode);
          if (st.data.state === 'picked_up') { showOpened(st.data.box_name); return; }
        } catch (e2) { /* fall through to the error */ }
      }
      setLoading(false);
      setError(e.response?.data?.detail || (isGeorgian ? 'ბოქსი ვერ გაიხსნა, სცადეთ თავიდან' : 'The box did not open, please try again'));
    }
  };

  const handleCheckCode = async () => {
    if (!pickCode || pickCode.length < 4) {
      setError(isGeorgian ? 'შეიყვანეთ სწორი გატანის კოდი' : 'Enter a valid pickup code');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await paymentAPI.calculate(pickCode);
      setParcelInfo(res.data);
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || (isGeorgian ? 'ამანათი ვერ მოიძებნა' : 'Parcel not found'));
    }
    setLoading(false);
  };

  const handlePay = async () => {
    setPaymentLoading(true);
    setError('');
    try {
      const res = await paymentAPI.create(pickCode);
      if (res.data.already_paid) {
        // Paid earlier but the box didn't open: open it, no second payment.
        setPaymentLoading(false);
        setStep(4);
        await handleOpenPaid();
        return;
      }
      // Save code for when they return from payment
      sessionStorage.setItem('pickup_code', pickCode);
      // Redirect to Keepz payment page
      if (res.data.payment_url) {
        window.location.href = res.data.payment_url;
      } else {
        setError(isGeorgian ? 'გადახდის ბმული ვერ მოიძებნა' : 'Payment URL not found');
      }
    } catch (e) {
      setError(e.response?.data?.detail || (isGeorgian ? 'გადახდის შეცდომა' : 'Payment error'));
    }
    setPaymentLoading(false);
  };

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.back} onClick={() => step === 2 ? setStep(1) : navigate('/')}>
          <ArrowLeft size={18} /> {t('common.back')}
        </div>
        <span style={s.headerTitle}>{t('parcel.pickupTitle')}</span>
        <div style={{ width: 60 }} />
      </header>
      <div style={s.content}>

        {/* STEP 1: Enter pickup code */}
        {step === 1 && (
          <>
            <div style={s.iconWrap}><Package size={36} color="#4d90fe" /></div>
            <h2 style={s.title}>{t('parcel.enterPickupCode')}</h2>
            <p style={s.sub}>{isGeorgian ? 'შეიყვანეთ ელ-ფოსტით ან SMS-ით მიღებული კოდი' : 'Enter the code you received via email or SMS'}</p>
            {error && <div style={s.error}>{error}</div>}

            <label style={s.label}>{t('parcel.pickupCode')}</label>
            <input
              id="pickup-code" name="pickup-code" type="text" inputMode="numeric" autoComplete="one-time-code"
              pattern="[0-9]*" enterKeyHint="go" aria-label={t('parcel.pickupCode')}
              style={s.codeInput}
              placeholder={isGeorgian ? '6-ნიშნა კოდი' : '6-digit code'}
              value={pickCode}
              onChange={e => setPickCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              autoFocus
            />

            <button style={{ ...s.btn, opacity: pickCode.length >= 4 ? 1 : 0.5 }}
              onClick={handleCheckCode} disabled={loading || pickCode.length < 4}>
              {loading ? <><Loader size={16} style={{animation:'spin 1s linear infinite'}} /> {t('common.loading')}</> : (isGeorgian ? 'გაგრძელება' : 'Continue')}
            </button>
          </>
        )}

        {/* STEP 2: Payment */}
        {step === 2 && parcelInfo && (
          <>
            <div style={s.iconWrap}><CreditCard size={36} color="#ffb020" /></div>
            <h2 style={{...s.title, textAlign:'center'}}>{isGeorgian ? 'გადახდა' : 'Payment'}</h2>
            <p style={{...s.sub, textAlign:'center'}}>{isGeorgian ? 'გადაიხადეთ ამანათის გატანის საფასური' : 'Pay the storage fee to open your locker'}</p>
            {error && <div style={s.error}>{error}</div>}

            <div style={s.payCard}>
              <div style={s.payRow}>
                <span style={s.payLabel}>{isGeorgian ? 'გამგზავნი' : 'Sender'}</span>
                <span style={s.payValue}>{parcelInfo.sender_name}</span>
              </div>
              <div style={s.payRow}>
                <span style={s.payLabel}>{isGeorgian ? 'ლოკერი' : 'Locker'}</span>
                <span style={s.payValue}>{parcelInfo.device_name}</span>
              </div>
              <div style={s.payRow}>
                <span style={s.payLabel}>{isGeorgian ? 'ბოქსის ზომა' : 'Box Size'}</span>
                <span style={s.payValue}>{parcelInfo.box_size === 'S' ? (isGeorgian ? 'პატარა' : 'Small') : parcelInfo.box_size === 'M' ? (isGeorgian ? 'საშუალო' : 'Medium') : (isGeorgian ? 'დიდი' : 'Large')}</span>
              </div>
              <div style={s.payRow}>
                <span style={s.payLabel}><Clock size={14} style={{marginRight:4,verticalAlign:'middle'}} />{isGeorgian ? 'შენახვის დრო' : 'Storage Time'}</span>
                <span style={s.payValue}>{parcelInfo.duration}</span>
              </div>
              <div style={{...s.payRow, borderTop:'1px solid #252733', paddingTop:16, marginTop:8}}>
                <span style={{...s.payLabel, fontSize:16, fontWeight:700, color:'#e4e5ed'}}>{isGeorgian ? 'სულ გადასახდელი' : 'Total'}</span>
                <span style={{fontSize:28, fontWeight:800, color:'#8BC63E', fontFamily:"'Space Mono', monospace"}}>{parcelInfo.price.toFixed(2)} ₾</span>
              </div>
            </div>

            <p style={{fontSize:11, color: '#8a8da4', textAlign:'center', marginBottom:16}}>
              {isGeorgian ? `ტარიფი: S=${pricing.S.toFixed(2)}₾/სთ, M=${pricing.M.toFixed(2)}₾/სთ, L=${pricing.L.toFixed(2)}₾/სთ` : `Rate: S=${pricing.S.toFixed(2)}₾/h, M=${pricing.M.toFixed(2)}₾/h, L=${pricing.L.toFixed(2)}₾/h`}
            </p>

            {parcelInfo.paid ? (
              <button style={s.payBtn} onClick={() => { setStep(4); handleOpenPaid(); }} disabled={loading}>
                <Box size={18} /> {isGeorgian ? 'უკვე გადახდილია — ბოქსის გახსნა' : 'Already paid — open box'}
              </button>
            ) : (
            <button style={s.payBtn} onClick={handlePay} disabled={paymentLoading}>
              {paymentLoading ? <><Loader size={16} style={{animation:'spin 1s linear infinite'}} /> {t('common.loading')}</> : (
                <><CreditCard size={18} /> {isGeorgian ? `გადახდა ${parcelInfo.price.toFixed(2)} ₾` : `Pay ${parcelInfo.price.toFixed(2)} ₾`}</>
              )}
            </button>
            )}
          </>
        )}

        {/* STEP 4: Confirming payment (after returning from Keepz) */}
        {step === 4 && (
          <>
            <div style={s.iconWrap}>
              {loading ? <Loader size={36} color="#4d90fe" style={{animation:'spin 1s linear infinite'}} /> : paidNotOpened ? <Check size={36} color="#8BC63E" /> : <Clock size={36} color="#ffb020" />}
            </div>
            {paidNotOpened ? (
              <>
                <h2 style={{ ...s.title, textAlign: 'center' }}>{isGeorgian ? 'გადახდა მიღებულია' : 'Payment received'}</h2>
                <p style={{ ...s.sub, textAlign: 'center' }}>
                  {isGeorgian
                    ? 'ბოქსი ჯერ არ გაიხსნა. დააჭირეთ „ბოქსის გახსნას“ — ხელახლა გადახდა არ დაგჭირდებათ.'
                    : 'The box has not opened yet. Press "Open box" — you will not be charged again.'}
                </p>
              </>
            ) : (
              <>
                <h2 style={{ ...s.title, textAlign: 'center' }}>{isGeorgian ? 'გადახდის დადასტურება…' : 'Confirming payment…'}</h2>
                <p style={{ ...s.sub, textAlign: 'center' }}>
                  {isGeorgian ? 'ბოქსი რამდენიმე წამში გაიხსნება. გთხოვთ, არ დახუროთ ეს გვერდი.' : 'Your box will open in a few seconds. Please keep this page open.'}
                </p>
              </>
            )}
            {error && <div style={s.error}>{error}</div>}
            {!loading && paidNotOpened && (
              <button style={s.payBtn} onClick={handleOpenPaid}>
                <Box size={18} /> {isGeorgian ? 'ბოქსის გახსნა' : 'Open box'}
              </button>
            )}
            {!loading && !paidNotOpened && (
              <>
                <button style={s.btn} onClick={() => pollPayment(pickCode)}>
                  <RefreshCw size={16} /> {isGeorgian ? 'თავიდან შემოწმება' : 'Check again'}
                </button>
                <button style={s.outlineBtn} onClick={() => navigate('/')}>{isGeorgian ? 'მთავარი გვერდი' : 'Home'}</button>
              </>
            )}
          </>
        )}

        {/* STEP 3: Success */}
        {step === 3 && (
          <>
            <div style={s.successIcon}><Check size={32} color="#8BC63E" /></div>
            <h2 style={{ ...s.title, textAlign: 'center' }}>{isGeorgian ? 'ბოქსი გაიხსნა!' : 'Box Opened!'}</h2>
            {result?.box_name && result.box_name !== '?' && (
              <div style={s.boxBadge}>{isGeorgian ? 'ბოქსი' : 'Box'} {result.box_name}</div>
            )}
            <p style={{ ...s.sub, textAlign: 'center', marginBottom: 20 }}>{isGeorgian ? 'აიღეთ თქვენი ამანათი' : 'Take your parcel'}</p>

            {doorOpen === false ? (
              <div style={s.doorDone} role="status">
                <div style={s.doorWarnIcon}><Check size={44} color="#8BC63E" /></div>
                <div style={{ ...s.doorWarnTitle, color: '#8BC63E' }}>{isGeorgian ? 'კარი დაიხურა' : 'Door closed'}</div>
                <div style={s.doorWarnText}>{isGeorgian ? 'გმადლობთ!' : 'Thank you!'}</div>
              </div>
            ) : (
              <div style={s.doorWarn} role="alert">
                <div style={s.doorWarnIcon}><AlertTriangle size={44} color="#ffb020" /></div>
                <div style={s.doorWarnTitle}>{isGeorgian ? 'დახურეთ კარი!' : 'CLOSE THE DOOR!'}</div>
                <div style={s.doorWarnText}>
                  {isGeorgian
                    ? 'ამანათის აღების შემდეგ მაგრად მიხურეთ ბოქსის კარი, სანამ არ ჩაიკეტება.'
                    : 'After taking your parcel, push the box door firmly shut until it clicks locked.'}
                </div>
                {doorOpen === true && (
                  <div style={s.doorStill}>{isGeorgian ? '● კარი ჯერ კიდევ ღიაა' : '● The door is still open'}</div>
                )}
              </div>
            )}

            <button style={s.outlineBtn} onClick={() => navigate('/')}>{isGeorgian ? 'დასრულება' : 'Done'}</button>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#07080c', color: '#e4e5ed' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #252733' },
  back: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#8789a0', cursor: 'pointer' },
  headerTitle: { fontSize: 15, fontWeight: 600 },
  content: { maxWidth: 420, margin: '0 auto', padding: '28px 16px 40px' },
  iconWrap: { width: 72, height: 72, borderRadius: 20, background: 'rgba(77,144,254,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' },
  title: { fontSize: 24, fontWeight: 700, marginBottom: 6 },
  sub: { fontSize: 14, color: '#8789a0', marginBottom: 28 },
  doorWarn: { background: 'rgba(255,176,32,0.1)', border: '2px solid #ffb020', borderRadius: 14, padding: '20px', marginBottom: 20, textAlign: 'left' },
  doorDone: { background: 'rgba(139,198,62,0.1)', border: '2px solid #8BC63E', borderRadius: 14, padding: '20px', marginBottom: 20, textAlign: 'center' },
  doorStill: { marginTop: 12, textAlign: 'center', fontSize: 14, fontWeight: 700, color: '#ff4466' },
  doorWarnIcon: { display: 'flex', justifyContent: 'center', marginBottom: 8 },
  doorWarnTitle: { fontSize: 30, fontWeight: 900, color: '#ffb020', textAlign: 'center', marginBottom: 10, letterSpacing: 0.5, lineHeight: 1.2 },
  doorWarnText: { fontSize: 16, color: '#e4e5ed', textAlign: 'center', lineHeight: 1.5 },
  doorWarnList: { listStyle: 'none', padding: 0, margin: 0, fontSize: 13, color: '#c0c2d0', lineHeight: 1.8 },
  error: { background: 'rgba(255,68,102,0.1)', border: '1px solid rgba(255,68,102,0.2)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#ff4466', marginBottom: 16 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#8789a0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, marginTop: 16 },
  codeInput: { width: '100%', background: '#161821', border: '1px solid #5c6078', borderRadius: 14, padding: '16px 12px', fontSize: 28, fontWeight: 700, fontFamily: "'Space Mono', monospace", color: '#6ea6ff', WebkitTextFillColor: '#6ea6ff', textAlign: 'center', letterSpacing: 10, outline: 'none', boxSizing: 'border-box' },
  btn: { width: '100%', padding: '15px 0', background: '#2f6fe0', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
  payCard: { background: '#0f1117', border: '1px solid #252733', borderRadius: 16, padding: '20px 18px', marginBottom: 20 },
  payRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' },
  payLabel: { fontSize: 13, color: '#8789a0', display: 'flex', alignItems: 'center' },
  payValue: { fontSize: 14, fontWeight: 600, color: '#e4e5ed' },
  payBtn: { width: '100%', padding: '16px 0', background: 'linear-gradient(135deg, #8BC63E, #00b4d8)', color: '#07080c', border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 },
  boxBadge: { textAlign: 'center', fontSize: 40, fontWeight: 900, color: '#8BC63E', fontFamily: "'Space Mono', monospace", margin: '4px 0 8px' },
  successIcon: { width: 72, height: 72, borderRadius: '50%', background: 'rgba(139,198,62,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' },
  outlineBtn: { width: '100%', padding: '14px 0', background: 'transparent', border: '1px solid #252733', borderRadius: 12, fontSize: 14, fontWeight: 600, color: '#8789a0', cursor: 'pointer', marginTop: 16 },
};
